import { z } from 'zod';
import type { LoyaltyPointsAdjustmentToken } from '@sleed/bizcommerce-api';

/** The most points one adjustment adds or takes away (PointCustomerController.MaxAdjustment). */
export const MAX_ADJUSTMENT = 100000;

/** The longest note the server stores (LoyaltyPointEvents.NoteMaxLength). */
export const ADJUSTMENT_NOTE_MAX = 200;

/**
 * An admin's adjustment of a customer's points, with the server's rules (PointCustomer/AdjustPoints): whole points
 * other than 0, at most 100 000 either way, a reason of up to 200 characters, and an expiry only for points added, in
 * the future (empty: the standard expiry).
 */
export const loyaltyAdjustmentSchema = z
	.object({
		cid: z.number({ message: 'validation.required' }).int(),
		pnt: z
			.number({ message: 'validation.required' })
			.int('loyalty.customers.adjust.pointsInvalid')
			.refine(p => p !== 0 && Math.abs(p) <= MAX_ADJUSTMENT, 'loyalty.customers.adjust.pointsInvalid'),
		nt: z.string().trim().min(1, 'validation.required').max(ADJUSTMENT_NOTE_MAX, 'validation.tooLong'),
		exdt: z.string().nullable()
	})
	.superRefine((form, ctx) => {
		if (!form.exdt) return;
		if (form.pnt < 0 || new Date(form.exdt) <= new Date())
			ctx.addIssue({ code: 'custom', path: ['exdt'], message: 'loyalty.customers.adjust.expiryInvalid' });
	});

export type LoyaltyAdjustmentFormValues = z.infer<typeof loyaltyAdjustmentSchema>;

export function adjustmentToToken(form: LoyaltyAdjustmentFormValues): LoyaltyPointsAdjustmentToken {
	return { cid: form.cid, pnt: form.pnt, nt: form.nt.trim(), exdt: form.pnt > 0 && form.exdt ? form.exdt : null };
}
